// Helper comun pentru cele 3 straturi de overlay ale temei (meniu mobil, drawer de filtre,
// Filament Finder). Incarcat primul (theme.js e primul <script defer>, vezi theme.liquid),
// deci disponibil global pentru facets.js si filament-finder.js.
// (1) Scroll-lock cu numarator, nu boolean simplu - bug real gasit: fiecare din cele 3
// componente isi seta propriul document.body.style.overflow independent, fara sa verifice
// daca alt overlay era inca deschis; inchiderea celui de-al doilea deschis re-activa scroll-ul
// de fundal in timp ce primul ramanea vizibil deschis (ex. deschizi meniul mobil, din el
// deschizi Filament Finder, inchizi doar finder-ul -> body scroll revine desi meniul e inca pe
// ecran).
// (2) Focus-trap minimal + restaurare focus pe elementul care a deschis overlay-ul - niciunul
// dintre cele 3 nu avea asta (verificat: zero `.focus(`/`activeElement`/`tabindex` in tot
// codul JS al temei) desi toate 3 se declara `role="dialog" aria-modal="true"` in Liquid.
window.TDOverlay = (function () {
  let lockCount = 0;
  function lock() {
    lockCount += 1;
    if (lockCount === 1) document.body.style.overflow = 'hidden';
  }
  function unlock() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = '';
  }

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function trapFocus(panel) {
    const trigger = document.activeElement;
    function focusables() {
      return Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    }
    const first = focusables()[0];
    if (first) first.focus();

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    }
    panel.addEventListener('keydown', onKeydown);

    return function release() {
      panel.removeEventListener('keydown', onKeydown);
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
  }

  return { lock, unlock, trapFocus };
})();

// Normalizeaza o eticheta de culoare la cheia folosita in color-map.js: scoate orice paranteza
// finala, nu doar "(Refill)"/"(Spool)" - Search & Discovery disambiguizeaza numele de culoare
// identice intre familii diferite adaugand "(Familie A - Familie B)" (ex. "Flesh (TPU 90A -
// TPU 85A)"), pe langa "(Refill)"/"(Spool)" din optiunile de produs individuale. O singura
// functie, folosita atat de VariantPicker (pagina de produs) cat si de facets.js (drawer de
// filtre) - bug real gasit: drawer-ul de filtre facea stripping doar pentru "(Refill)"/"(Spool)"
// (in Liquid), deci orice valoare disambiguizata pe familie rata mereu lookup-ul in color-map.js
// si cadea pe cercul generic gri, indiferent de culoarea reala.
window.TDColorKey = function (label) {
  return String(label || '').replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase();
};

class ColorExplorer extends HTMLElement {
  connectedCallback() {
    this.status = this.querySelector('[data-color-status]');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-color-name]');
      if (!button) return;
      this.querySelectorAll('[aria-pressed="true"]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
      button.setAttribute('aria-pressed', 'true');
      if (this.status) this.status.textContent = button.dataset.colorName;
      this.dispatchEvent(new CustomEvent('3done:color-change', { bubbles: true, detail: { color: button.dataset.colorName } }));
    });
  }
}

if (!customElements.get('color-explorer')) customElements.define('color-explorer', ColorExplorer);

class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dotsWrap = this.querySelector('[data-carousel-dots]');
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.index = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.dotsWrap) {
      this.slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Produsul ${i + 1} din ${this.slides.length}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(dot);
      });
      this.dots = Array.from(this.dotsWrap.children);
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1));

    let scrollTimer;
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.syncFromScroll(), 120);
      },
      { passive: true }
    );

    // Bug real gasit: 'touchstart' pauza autoplay, dar 'mouseleave'/'focusout' nu se declanseaza
    // niciodata fiabil dupa o atingere pe mobil - odata ce utilizatorul atingea caruselul,
    // autoplay ramanea permanent oprit. 'touchend'/'touchcancel' reiau explicit dupa atingere.
    ['mouseenter', 'focusin', 'touchstart'].forEach((evt) => this.addEventListener(evt, () => this.pause(), { passive: true }));
    ['mouseleave', 'focusout', 'touchend', 'touchcancel'].forEach((evt) => this.addEventListener(evt, () => this.resume(), { passive: true }));

    this.updateDots();
    if (!this.reducedMotion && this.slides.length > 1) {
      this.play();
      // Fara rost sa avansam un carusel pe care nimeni nu-l vede.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((e) => (e.isIntersecting ? this.resume() : this.pause())),
          { threshold: 0.15 }
        );
        this.observer.observe(this);
      }
    }
  }

  goTo(i) {
    this.index = (i + this.slides.length) % this.slides.length;
    const slide = this.slides[this.index];
    // Bug real: `scrollIntoView` aduce slide-ul in ecran derulant cel mai apropiat
    // container derulabil - iar cand caruselul e deja iesit din ecran (utilizatorul a
    // derulat in jos), acela e PAGINA. Autoplay-ul o arunca astfel inapoi la hero la
    // fiecare 4,2 secunde. Scriem direct pe pista orizontala: nu atinge niciodata
    // scroll-ul paginii, indiferent unde se afla caruselul.
    const delta = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    this.updateDots();
  }

  syncFromScroll() {
    const trackRect = this.track.getBoundingClientRect();
    let closest = 0;
    let closestDist = Infinity;
    this.slides.forEach((slide, i) => {
      const dist = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    this.index = closest;
    this.updateDots();
  }

  updateDots() {
    if (!this.dots) return;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
  }

  play() {
    clearInterval(this.timer);   // altfel fiecare resume() lasa in urma un timer viu
    this.timer = setInterval(() => this.goTo(this.index + 1), 4200);
  }

  pause() {
    clearInterval(this.timer);
  }

  resume() {
    if (!this.reducedMotion && this.slides.length > 1) this.play();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    clearInterval(this.timer);
  }
}

if (!customElements.get('product-carousel')) customElements.define('product-carousel', ProductCarousel);

/*
 * Selector generic de variante, pentru orice tip de produs (nu doar filamente).
 * Optiunile Shopify sunt clasificate server-side (main-product.liquid) dupa numele lor -
 * culoare -> grid de swatch-uri, alegere scurta (ambalaj/model/flow/diametru/material) ->
 * pastile, orice altceva -> select - dar logica de selectie/potrivire varianta e aceeasi
 * pentru toate, aici, o singura data.
 *
 * Fiecare axa de optiune are UN SINGUR wrapper cu `data-option-group="{index}"` - singurul
 * element pe care il cauta `.closest()`. Butoanele/pastilele din interior NU mai poarta acest
 * atribut (l-au purtat intr-o versiune anterioara, ceea ce facea ca `.closest()` chemat DE PE
 * un buton sa se opreasca pe butonul insusi, nu pe grup - de-aia la selectarea unei culori
 * starea "apasat" nu se stergea de pe celelalte, puteau parea mai multe active simultan).
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const jsonEl = this.querySelector('[data-variant-json]');
    this.variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
    this.optionGroups = Array.from(this.querySelectorAll('[data-option-group]'));
    this.mediaImg = document.getElementById(this.dataset.mediaTarget);
    this.priceEl = document.querySelector('[data-product-price]');
    this.idInput = this.querySelector('[data-variant-id-input]');
    this.submitBtn = this.querySelector('[data-add-to-cart]');
    this.submitBtnText = this.querySelector('[data-add-to-cart-text]');
    this.availabilityEl = document.querySelector('[data-availability]');
    this.productTitle = this.dataset.productTitle || '';

    this.applyColorMap();
    this.selected = this.readInitialSelection();
    this.packagingGroup = this.optionGroups.find((group) => this.isPackagingGroup(group));
    this.compatibilityNotice = this.createCompatibilityNotice();

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.td-color-swatch,.td-pill');
      if (!trigger || !this.contains(trigger)) return;
      const group = trigger.closest('[data-option-group]');
      if (!group) return;
      const packagingSelection = this.isPackagingGroup(group);
      if (trigger.classList.contains('is-incompatible') && !packagingSelection) return;
      event.preventDefault();
      const index = Number(group.dataset.optionGroup);
      const previousSelection = this.selected.slice();
      group.querySelectorAll('[aria-pressed]').forEach((el) => {
        el.setAttribute('aria-pressed', 'false');
        el.classList.remove('is-selected');
      });
      trigger.setAttribute('aria-pressed', 'true');
      trigger.classList.add('is-selected');
      this.selected[index] = trigger.dataset.value;
      if (packagingSelection) this.resolvePackagingConflict(index, previousSelection);
      else this.hideCompatibilityNotice();
      this.updateUI(true);
    });

    this.addEventListener('change', (event) => {
      const select = event.target.closest('select');
      if (!select || !this.contains(select)) return;
      const group = select.closest('[data-option-group]');
      if (!group) return;
      this.selected[Number(group.dataset.optionGroup)] = select.value;
      this.updateUI(true);
    });

    this.updateUI(false);
  }

  readInitialSelection() {
    const selected = [];
    this.optionGroups.forEach((group) => {
      const index = Number(group.dataset.optionGroup);
      const pressed = group.querySelector('[aria-pressed="true"]');
      const select = group.querySelector('select');
      if (pressed) selected[index] = pressed.dataset.value;
      else if (select) selected[index] = select.value;
    });
    return selected;
  }

  applyColorMap() {
    const map = window.TD_COLOR_MAP;
    if (!map) return;
    const family = (map.by_family && map.by_family[this.dataset.familySlug]) || {};
    this.querySelectorAll('.td-color-swatch').forEach((swatch) => {
      if (swatch.style.getPropertyValue('--swatch-color')) return; // deja rezolvat server-side (metafield)
      const key = window.TDColorKey(swatch.dataset.value);
      const hex = family[key] || (map.flat && map.flat[key]);
      if (hex) swatch.style.setProperty('--swatch-color', hex);
    });
  }

  isPackagingGroup(group) {
    const label = group.querySelector('.td-option__label')?.textContent || '';
    const ariaLabel = group.querySelector('[role="group"]')?.getAttribute('aria-label') || '';
    return /ambalare|packaging/i.test(`${label} ${ariaLabel}`);
  }

  createCompatibilityNotice() {
    if (!this.packagingGroup) return null;
    const notice = document.createElement('p');
    notice.className = 'td-variant-compatibility-note';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.hidden = true;
    this.packagingGroup.insertAdjacentElement('afterend', notice);
    return notice;
  }

  hideCompatibilityNotice() {
    if (this.compatibilityNotice) this.compatibilityNotice.hidden = true;
  }

  clearGroupSelection(group) {
    const axisIndex = Number(group.dataset.optionGroup);
    this.selected[axisIndex] = undefined;
    group.querySelectorAll('[aria-pressed]').forEach((control) => {
      control.setAttribute('aria-pressed', 'false');
      control.classList.remove('is-selected');
    });
    const select = group.querySelector('select');
    if (select) select.selectedIndex = -1;
  }

  resolvePackagingConflict(packagingAxis, previousSelection) {
    const exactVariantExists = this.variants.some((variant) =>
      variant.options.every((value, index) => this.selected[index] === undefined || value === this.selected[index])
    );
    if (exactVariantExists) {
      this.hideCompatibilityNotice();
      return;
    }

    const previousColor = previousSelection.find((_, index) => index !== packagingAxis);
    this.optionGroups.forEach((group) => {
      if (Number(group.dataset.optionGroup) !== packagingAxis) this.clearGroupSelection(group);
    });

    if (this.compatibilityNotice) {
      const packagingValue = this.selected[packagingAxis];
      const isRomanian = (document.documentElement.lang || '').toLowerCase().startsWith('ro');
      this.compatibilityNotice.textContent = isRomanian
        ? `${previousColor} nu este disponibil ${packagingValue === 'Cu spool' ? 'cu spool' : 'ca refill'}. Am păstrat formatul ales; selectează una dintre nuanțele disponibile.`
        : `${previousColor} is not available as ${packagingValue}. The format remains selected; choose one of the available colors.`;
      this.compatibilityNotice.hidden = false;
    }

    const fallback = this.variants.find(
      (variant) => variant.options[packagingAxis] === this.selected[packagingAxis] && variant.available
    ) || this.variants.find((variant) => variant.options[packagingAxis] === this.selected[packagingAxis]);
    if (fallback && this.priceEl) this.priceEl.textContent = fallback.price;
  }

  findMatchingVariant() {
    const hasMissingSelection = this.optionGroups.some(
      (group) => this.selected[Number(group.dataset.optionGroup)] === undefined
    );
    if (hasMissingSelection) return undefined;
    return this.variants.find((variant) =>
      variant.options.every((value, index) => this.selected[index] === undefined || value === this.selected[index])
    );
  }

  /* Marcheaza cu .is-incompatible orice valoare care, combinata cu restul selectiei curente,
   * nu corespunde niciunei variante reale Shopify - ex. daca ai ales imprimanta "X2D", optiunea
   * de material "Stainless Steel" se dezactiveaza daca acea combinatie nu exista printre variante. */
  updateAvailabilityStates() {
    this.optionGroups.forEach((group) => {
      const axisIndex = Number(group.dataset.optionGroup);
      group.querySelectorAll('[data-value]').forEach((control) => {
        const candidate = control.dataset.value;
        const compatible = this.isPackagingGroup(group) || this.variants.some(
          (variant) =>
            variant.options[axisIndex] === candidate &&
            variant.options.every((value, index) => index === axisIndex || this.selected[index] === undefined || value === this.selected[index])
        );
        control.classList.toggle('is-incompatible', !compatible);
        if (control.tagName === 'OPTION') control.disabled = !compatible && !control.selected;
      });
    });
  }

  updateUI(pushHistory) {
    this.updateAvailabilityStates();
    const variant = this.findMatchingVariant();

    if (variant) {
      if (this.priceEl) this.priceEl.textContent = variant.price;
      if (this.idInput) this.idInput.value = variant.id;
      if (this.mediaImg && variant.featuredImage) {
        this.mediaImg.src = variant.featuredImage;
        // un <img> cu srcset ignora orice schimbare facuta doar pe .src (browserul continua
        // sa aleaga din srcset-ul vechi) - de-aia trebuie scos explicit la schimbarea variantei.
        this.mediaImg.removeAttribute('srcset');
        this.mediaImg.alt = variant.title ? `${this.productTitle} — ${variant.title}` : this.productTitle;
        // sincronizeaza thumbnail-ul activ din caruselul de sub imagine, daca exista unul
        document.querySelectorAll('[data-thumb]').forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb.dataset.imageUrl === variant.featuredImage);
        });
      }
      if (this.submitBtn) {
        this.submitBtn.disabled = !variant.available;
        if (this.submitBtnText) {
          this.submitBtnText.textContent = variant.available ? this.submitBtn.dataset.addLabel : this.submitBtn.dataset.soldLabel;
        }
      }
      // Termenul de livrare tine de varianta, nu de produs: aceeasi gama poate avea nuante
      // pe stoc si nuante pe precomanda. Daca ramane textul variantei anterioare, clientul
      // cumpara crezand ca primeste in 24-48h.
      if (this.availabilityEl) {
        const el = this.availabilityEl;
        el.hidden = !variant.available;
        el.classList.toggle('td-availability--preorder', Boolean(variant.preorder));
        el.textContent = variant.preorder ? el.dataset.preorderLabel : el.dataset.inStockLabel;
      }
      if (pushHistory && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    } else {
      if (this.idInput) this.idInput.value = '';
      if (this.submitBtn) this.submitBtn.disabled = true;
      if (pushHistory && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }
    }
  }

  /* Apelata din afara componentei (caruselul de thumbnails) cand utilizatorul da click pe
   * o miniatura asociata clar unei variante - sincronizeaza toate controalele (swatch/pill/
   * select) fara sa duplice logica de mai sus, doar recalculeaza selectia si cheama updateUI. */
  selectVariantById(id) {
    const variant = this.variants.find((v) => String(v.id) === String(id));
    if (!variant) return;
    this.hideCompatibilityNotice();
    this.selected = variant.options.slice();
    this.optionGroups.forEach((group) => {
      const axisIndex = Number(group.dataset.optionGroup);
      const value = this.selected[axisIndex];
      group.querySelectorAll('[data-value]').forEach((el) => {
        const match = el.dataset.value === value;
        if (el.tagName === 'OPTION') {
          el.selected = match;
        } else {
          el.setAttribute('aria-pressed', match ? 'true' : 'false');
          el.classList.toggle('is-selected', match);
        }
      });
    });
    this.updateUI(true);
  }
}

if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

/*
 * Carusel de thumbnails sub imaginea principala a produsului (product.media). Click pe o
 * miniatura: schimba imediat imaginea mare; daca miniatura e asociata clar unei variante
 * (data-variant-ids), selecteaza acea varianta prin variant-picker (nu duplica logica de
 * potrivire - apeleaza metoda expusa acolo). Sincronizarea inversa (varianta -> thumbnail
 * activ) se face in VariantPicker.updateUI, ca sa existe un singur loc care asculta
 * schimbarea variantei.
 */
class ThumbRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-thumb-track]');
    this.prevBtn = this.querySelector('[data-thumb-prev]');
    this.nextBtn = this.querySelector('[data-thumb-next]');
    this.mediaImg = document.getElementById(this.dataset.mediaTarget);

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-thumb]');
      if (!thumb || !this.contains(thumb)) return;
      const picker = document.querySelector('variant-picker');
      const variantIds = (thumb.dataset.variantIds || '').split(',').filter(Boolean);
      if (picker && variantIds.length) {
        picker.selectVariantById(variantIds[0]);
      } else if (this.mediaImg && thumb.dataset.imageUrl) {
        this.mediaImg.src = thumb.dataset.imageUrl;
        this.mediaImg.removeAttribute('srcset');
        this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      }
    });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollBy(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollBy(1));
  }

  scrollBy(direction) {
    if (!this.track) return;
    const amount = this.track.clientWidth * 0.8 * direction;
    // Bug real gasit: un `behavior` explicit trecut la scrollBy are prioritate fata de CSS
    // `scroll-behavior` - regula globala `*{scroll-behavior:auto!important}` din base.css
    // (sub prefers-reduced-motion) NU anuleaza un 'smooth' hardcodat aici.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: amount, behavior: reducedMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('thumb-rail')) customElements.define('thumb-rail', ThumbRail);

// Swipe pe imaginea principala a galeriei de produs: bug real gasit - imaginea mare nu avea
// nicio modalitate de navigare prin swipe pe mobil (doar click pe miniaturi sau sagetile mici
// ale thumb-rail-ului), desi brief-ul cere explicit swipe pe galerie. Nu duplica logica de
// selectie - gaseste miniatura activa (.is-active, intretinuta deja de ThumbRail/VariantPicker)
// si simuleaza click pe cea anterioara/urmatoare, reutilizand 100% codul existent.
(function () {
  const media = document.querySelector('[data-product-media]');
  if (!media) return;
  let startX = 0;
  let startY = 0;
  let tracking = false;

  media.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return;
    startX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
    tracking = true;
  }, { passive: true });

  media.addEventListener('touchend', (event) => {
    if (!tracking) return;
    tracking = false;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;
    // prag minim + swipe clar orizontal (nu un scroll vertical al paginii)
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    const thumbs = Array.from(document.querySelectorAll('[data-thumb]'));
    if (!thumbs.length) return;
    const activeIndex = thumbs.findIndex((t) => t.classList.contains('is-active'));
    const current = activeIndex === -1 ? 0 : activeIndex;
    const nextIndex = dx < 0 ? (current + 1) % thumbs.length : (current - 1 + thumbs.length) % thumbs.length;
    thumbs[nextIndex].click();
  }, { passive: true });
})();

// Meniu mobil (hamburger): la >900px navigarea reala e vizibila direct in header (.td-nav);
// sub acel prag .td-nav e ascunsa complet (CSS), deci acest panel e SINGURA cale de navigare
// pe mobil - bug real gasit si reparat (inainte nu exista nicio alternativa sub 900px).
(function () {
  const toggle = document.querySelector('[data-mobile-nav-toggle]');
  const panel = document.querySelector('[data-mobile-nav]');
  if (!toggle || !panel) return;
  let releaseFocus = null;

  function open() {
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    window.TDOverlay.lock();
    releaseFocus = window.TDOverlay.trapFocus(panel);
  }
  function close() {
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    window.TDOverlay.unlock();
    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
  }

  toggle.addEventListener('click', () => (panel.hidden ? open() : close()));
  panel.querySelectorAll('[data-mobile-nav-close]').forEach((el) => el.addEventListener('click', close));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) close(); });
  window.matchMedia('(min-width: 901px)').addEventListener('change', (e) => { if (e.matches) close(); });
})();

// Fotografiile de prezentare din descrierea de produs (urcate de upload_gallery_images.py)
// vin ca <p><img> separate, imprastiate prin text: una dupa lista de avantaje, una inainte
// de intrebari. Cand sunt cel putin doua, le adunam intr-un singur rand, la locul primeia,
// ca sa nu ceara doua ecrane de derulare (grila e in base.css, .td-rte-figures).
// Un produs cu o singura poza nu are pereche, deci ramane cum e: 50%, centrata, la locul ei.
// Nu atinge galeria de langa pret si nu modifica descrierea de pe Shopify - doar afisarea.
(function groupDescriptionFigures() {
  const rte = document.querySelector('.td-product__description');
  if (!rte) return;
  const paras = Array.from(rte.children).filter(
    (el) => el.tagName === 'P' && el.children.length === 1 && el.firstElementChild.tagName === 'IMG'
  );
  if (paras.length < 2) return;
  const row = document.createElement('div');
  row.className = 'td-rte-figures';
  paras.forEach((p) => row.appendChild(p.firstElementChild));
  rte.insertBefore(row, paras[0]);
  paras.forEach((p) => p.remove());
})();
